// Primary 6 English Learning Data - Part 1 (Topics 1-10)
// Based on Primary English Learning P6 Topics.txt

export const p6DataPart1 = {
  gradeId: 'p6',
  gradeName: 'Primary 6 (小學六年級)',
  objectives: [
    'Master advanced grammar structures and complex sentence patterns',
    'Develop sophisticated reading comprehension and analytical skills',
    'Learn advanced writing techniques and essay structures',
    'Master academic vocabulary and formal expressions',
    'Understand complex discourse and argumentative writing'
  ],
  keySkills: [
    'Advanced grammar and syntax',
    'Complex sentence structures',
    'Academic vocabulary and formal language',
    'Critical thinking and analysis',
    'Sophisticated writing and presentation skills'
  ],
  topics: [
    {
      id: 'p6-topic-1',
      title: 'Advanced Grammar Structures',
      description: 'Learning to use advanced grammar structures and complex patterns',
      purpose: 'To teach students how to use advanced grammar structures including subjunctive mood, conditional perfect, and complex verb forms.',
      questions: [
        {
          question: 'If I _____ (know) the answer, I would have told you.',
          answer: 'had known',
          explanation: 'Use "had + past participle" in the if-clause of third conditional.'
        },
        {
          question: 'She _____ (pass) the exam if she had studied harder.',
          answer: 'would have passed',
          explanation: 'Use "would have + past participle" in the main clause of third conditional.'
        },
        {
          question: 'I wish I _____ (be) taller.',
          answer: 'were',
          explanation: 'Use "were" (subjunctive) after "wish" to express an impossible desire.'
        },
        {
          question: 'If only he _____ (come) to the party.',
          answer: 'had come',
          explanation: 'Use "had + past participle" after "If only" to express regret about the past.'
        },
        {
          question: 'I suggest that she _____ (study) more.',
          answer: 'study',
          explanation: 'Use base form of verb after "suggest that" in subjunctive mood.'
        },
        {
          question: 'It is important that he _____ (be) on time.',
          answer: 'be',
          explanation: 'Use base form of verb after "important that" in subjunctive mood.'
        },
        {
          question: 'I would rather you _____ (go) home now.',
          answer: 'went',
          explanation: 'Use past simple after "would rather" to express preference.'
        },
        {
          question: 'If I _____ (have) more money, I would have bought a car.',
          answer: 'had had',
          explanation: 'Use "had had" (past perfect) in the if-clause of third conditional.'
        },
        {
          question: 'She _____ (graduate) if she had passed all her exams.',
          answer: 'would have graduated',
          explanation: 'Use "would have + past participle" in the main clause of third conditional.'
        },
        {
          question: 'I wish I _____ (can) speak French.',
          answer: 'could',
          explanation: 'Use "could" (past of "can") after "wish" to express an impossible desire.'
        }
      ]
    },
    {
      id: 'p6-topic-2',
      title: 'Complex Sentence Structures',
      description: 'Learning to construct complex sentences with multiple clauses',
      purpose: 'To teach students how to construct complex sentences with multiple clauses, including noun clauses, adjective clauses, and adverb clauses.',
      questions: [
        {
          question: 'Complete: "_____ he said was true."',
          answer: 'What',
          explanation: 'Use "What" to introduce a noun clause as the subject of the sentence.'
        },
        {
          question: 'Fill in the blank: "I don\'t know _____ he went."',
          answer: 'where',
          explanation: 'Use "where" to introduce a noun clause as the object of the verb.'
        },
        {
          question: 'Complete: "The book _____ I bought is interesting."',
          answer: 'which',
          explanation: 'Use "which" to introduce an adjective clause modifying a thing.'
        },
        {
          question: 'Fill in the blank: "I will call you _____ I arrive."',
          answer: 'when',
          explanation: 'Use "when" to introduce an adverb clause of time.'
        },
        {
          question: 'Complete: "_____ he was tired, he continued working."',
          answer: 'Although',
          explanation: 'Use "Although" to introduce an adverb clause of concession.'
        },
        {
          question: 'Fill in the blank: "I wonder _____ she is thinking."',
          answer: 'what',
          explanation: 'Use "what" to introduce a noun clause as the object of the verb.'
        },
        {
          question: 'Complete: "The man _____ lives next door is a doctor."',
          answer: 'who',
          explanation: 'Use "who" to introduce an adjective clause modifying a person.'
        },
        {
          question: 'Fill in the blank: "I will help you _____ you need it."',
          answer: 'if',
          explanation: 'Use "if" to introduce an adverb clause of condition.'
        },
        {
          question: 'Complete: "_____ he said made me angry."',
          answer: 'What',
          explanation: 'Use "What" to introduce a noun clause as the subject of the sentence.'
        },
        {
          question: 'Fill in the blank: "I will wait _____ you finish."',
          answer: 'until',
          explanation: 'Use "until" to introduce an adverb clause of time.'
        }
      ]
    },
    {
      id: 'p6-topic-3',
      title: 'Academic Vocabulary',
      description: 'Learning to use academic vocabulary and formal expressions',
      purpose: 'To teach students how to use academic vocabulary, formal expressions, and sophisticated language appropriate for academic writing.',
      questions: [
        {
          question: 'What is a synonym for "conclude"?',
          answer: 'determine',
          explanation: '"Determine" is a synonym for "conclude" - both mean to reach a decision.'
        },
        {
          question: 'What is a synonym for "demonstrate"?',
          answer: 'show',
          explanation: '"Show" is a synonym for "demonstrate" - both mean to prove or display.'
        },
        {
          question: 'What is a synonym for "indicate"?',
          answer: 'suggest',
          explanation: '"Suggest" is a synonym for "indicate" - both mean to point out or show.'
        },
        {
          question: 'What is a synonym for "establish"?',
          answer: 'create',
          explanation: '"Create" is a synonym for "establish" - both mean to set up or found.'
        },
        {
          question: 'What is a synonym for "maintain"?',
          answer: 'keep',
          explanation: '"Keep" is a synonym for "maintain" - both mean to continue or preserve.'
        },
        {
          question: 'What is a synonym for "obtain"?',
          answer: 'get',
          explanation: '"Get" is a synonym for "obtain" - both mean to acquire or receive.'
        },
        {
          question: 'What is a synonym for "provide"?',
          answer: 'supply',
          explanation: '"Supply" is a synonym for "provide" - both mean to give or furnish.'
        },
        {
          question: 'What is a synonym for "require"?',
          answer: 'need',
          explanation: '"Need" is a synonym for "require" - both mean to need or demand.'
        },
        {
          question: 'What is a synonym for "significant"?',
          answer: 'important',
          explanation: '"Important" is a synonym for "significant" - both mean meaningful or notable.'
        },
        {
          question: 'What is a synonym for "sufficient"?',
          answer: 'enough',
          explanation: '"Enough" is a synonym for "sufficient" - both mean adequate or satisfactory.'
        }
      ]
    },
    {
      id: 'p6-topic-4',
      title: 'Formal Writing',
      description: 'Learning to write in formal academic style',
      purpose: 'To teach students how to write in formal academic style with appropriate vocabulary, sentence structure, and tone.',
      questions: [
        {
          question: 'What is the purpose of formal writing?',
          answer: 'to communicate professionally',
          explanation: 'Formal writing aims to communicate professionally and appropriately in academic or business contexts.'
        },
        {
          question: 'What should you avoid in formal writing?',
          answer: 'contractions',
          explanation: 'Avoid contractions (don\'t, can\'t, won\'t) in formal writing - use full forms instead.'
        },
        {
          question: 'What type of language should you use in formal writing?',
          answer: 'precise and specific',
          explanation: 'Use precise and specific language in formal writing to convey exact meaning.'
        },
        {
          question: 'What should you include in formal writing?',
          answer: 'evidence and examples',
          explanation: 'Include evidence and examples to support your arguments in formal writing.'
        },
        {
          question: 'What tone should you use in formal writing?',
          answer: 'objective and professional',
          explanation: 'Use an objective and professional tone in formal writing.'
        },
        {
          question: 'What should you avoid in formal writing?',
          answer: 'slang and colloquialisms',
          explanation: 'Avoid slang and colloquialisms in formal writing - use standard English.'
        },
        {
          question: 'What should you include in formal writing?',
          answer: 'proper citations',
          explanation: 'Include proper citations when using information from other sources in formal writing.'
        },
        {
          question: 'What structure should formal writing have?',
          answer: 'clear organization',
          explanation: 'Formal writing should have clear organization with logical flow and structure.'
        },
        {
          question: 'What should you avoid in formal writing?',
          answer: 'personal opinions without support',
          explanation: 'Avoid personal opinions without support in formal writing - back up claims with evidence.'
        },
        {
          question: 'What should you include in formal writing?',
          answer: 'conclusion',
          explanation: 'Include a conclusion that summarizes main points and restates the thesis in formal writing.'
        }
      ]
    },
    {
      id: 'p6-topic-5',
      title: 'Argumentative Writing',
      description: 'Learning to write argumentative essays with evidence',
      purpose: 'To teach students how to write argumentative essays that present a clear position supported by evidence and reasoning.',
      questions: [
        {
          question: 'What is the purpose of argumentative writing?',
          answer: 'to persuade the reader',
          explanation: 'Argumentative writing aims to persuade the reader to accept the writer\'s position on an issue.'
        },
        {
          question: 'What should you include in an argumentative essay?',
          answer: 'clear thesis statement',
          explanation: 'Include a clear thesis statement that presents your position on the issue.'
        },
        {
          question: 'What should you provide to support your argument?',
          answer: 'evidence and examples',
          explanation: 'Provide evidence and examples to support your argument and make it convincing.'
        },
        {
          question: 'What should you address in argumentative writing?',
          answer: 'counterarguments',
          explanation: 'Address counterarguments to show you have considered opposing viewpoints.'
        },
        {
          question: 'What should you use to connect ideas in argumentative writing?',
          answer: 'transitions',
          explanation: 'Use transitions to connect ideas and help the argument flow logically.'
        },
        {
          question: 'What should you include in the conclusion of an argumentative essay?',
          answer: 'restatement of thesis',
          explanation: 'Include a restatement of the thesis and summary of main points in the conclusion.'
        },
        {
          question: 'What tone should you use in argumentative writing?',
          answer: 'logical and reasonable',
          explanation: 'Use a logical and reasonable tone in argumentative writing.'
        },
        {
          question: 'What should you avoid in argumentative writing?',
          answer: 'emotional language',
          explanation: 'Avoid overly emotional language in argumentative writing - focus on logic and evidence.'
        },
        {
          question: 'What should you include to make your argument stronger?',
          answer: 'specific examples',
          explanation: 'Include specific examples to make your argument stronger and more convincing.'
        },
        {
          question: 'What should you do with opposing viewpoints?',
          answer: 'acknowledge and refute',
          explanation: 'Acknowledge opposing viewpoints and then refute them with evidence and reasoning.'
        }
      ]
    },
    {
      id: 'p6-topic-6',
      title: 'Critical Analysis',
      description: 'Learning to analyze texts and arguments critically',
      purpose: 'To teach students how to analyze texts, arguments, and information critically to evaluate their validity and effectiveness.',
      questions: [
        {
          question: 'What is the purpose of critical analysis?',
          answer: 'to evaluate information',
          explanation: 'Critical analysis aims to evaluate information, arguments, and evidence to determine their validity.'
        },
        {
          question: 'What should you look for when analyzing an argument?',
          answer: 'evidence and reasoning',
          explanation: 'Look for evidence and reasoning when analyzing an argument to evaluate its strength.'
        },
        {
          question: 'What should you consider when analyzing a text?',
          answer: 'author\'s purpose',
          explanation: 'Consider the author\'s purpose when analyzing a text to understand their intentions.'
        },
        {
          question: 'What should you evaluate in critical analysis?',
          answer: 'bias and assumptions',
          explanation: 'Evaluate bias and assumptions in critical analysis to identify potential weaknesses.'
        },
        {
          question: 'What should you look for in evidence?',
          answer: 'reliability and relevance',
          explanation: 'Look for reliability and relevance in evidence to determine its quality.'
        },
        {
          question: 'What should you consider when analyzing sources?',
          answer: 'credibility',
          explanation: 'Consider the credibility of sources when analyzing information.'
        },
        {
          question: 'What should you evaluate in reasoning?',
          answer: 'logic and consistency',
          explanation: 'Evaluate logic and consistency in reasoning to identify flaws in arguments.'
        },
        {
          question: 'What should you look for in conclusions?',
          answer: 'supporting evidence',
          explanation: 'Look for supporting evidence in conclusions to determine if they are justified.'
        },
        {
          question: 'What should you consider when analyzing tone?',
          answer: 'author\'s attitude',
          explanation: 'Consider the author\'s attitude when analyzing tone to understand their perspective.'
        },
        {
          question: 'What should you evaluate in language use?',
          answer: 'effectiveness and clarity',
          explanation: 'Evaluate effectiveness and clarity in language use to determine communication quality.'
        }
      ]
    },
    {
      id: 'p6-topic-7',
      title: 'Advanced Punctuation',
      description: 'Learning to use advanced punctuation marks correctly',
      purpose: 'To teach students how to use advanced punctuation marks including semicolons, colons, dashes, and parentheses in complex sentences.',
      questions: [
        {
          question: 'Complete: "I love reading; _____ I spend hours in the library."',
          answer: 'therefore',
          explanation: 'Use semicolon to connect two related independent clauses.'
        },
        {
          question: 'Fill in the blank: "There are three things I need: _____, _____, and _____."',
          answer: 'a pen, paper, time',
          explanation: 'Use colon to introduce a list of items.'
        },
        {
          question: 'Complete: "The weather was terrible _____ it was raining all day."',
          answer: '—',
          explanation: 'Use dash to emphasize additional information.'
        },
        {
          question: 'Fill in the blank: "My friend _____ who lives in Hong Kong _____ is coming to visit."',
          answer: '(, )',
          explanation: 'Use parentheses to add non-essential information.'
        },
        {
          question: 'Complete: "She was tired; _____ she continued working."',
          answer: 'however',
          explanation: 'Use semicolon to connect two independent clauses with a conjunctive adverb.'
        },
        {
          question: 'Fill in the blank: "I have one goal: _____."',
          answer: 'to succeed',
          explanation: 'Use colon to introduce an explanation or clarification.'
        },
        {
          question: 'Complete: "The movie was excellent _____ the acting was superb."',
          answer: '—',
          explanation: 'Use dash to emphasize additional information.'
        },
        {
          question: 'Fill in the blank: "My brother _____ the one who studies medicine _____ is very busy."',
          answer: '(, )',
          explanation: 'Use parentheses to add non-essential information.'
        },
        {
          question: 'Complete: "I studied hard; _____ I passed the exam."',
          answer: 'consequently',
          explanation: 'Use semicolon to connect two independent clauses with a conjunctive adverb.'
        },
        {
          question: 'Fill in the blank: "There are two options: _____ or _____."',
          answer: 'stay, leave',
          explanation: 'Use colon to introduce a list of alternatives.'
        }
      ]
    },
    {
      id: 'p6-topic-8',
      title: 'Advanced Tenses and Aspects',
      description: 'Learning to use advanced tenses and aspects in complex contexts',
      purpose: 'To teach students how to use advanced tenses and aspects including perfect continuous forms and mixed conditionals in complex contexts.',
      questions: [
        {
          question: 'I _____ (study) English for six years by next month.',
          answer: 'will have been studying',
          explanation: 'Use "will have been + verb-ing" for future perfect continuous.'
        },
        {
          question: 'She _____ (work) here for fifteen years before she retires.',
          answer: 'will have worked',
          explanation: 'Use "will have + past participle" for future perfect.'
        },
        {
          question: 'If I _____ (study) harder, I would have passed the exam.',
          answer: 'had studied',
          explanation: 'Use "had + past participle" in the if-clause of third conditional.'
        },
        {
          question: 'He _____ (live) in Hong Kong for ten years when he moves.',
          answer: 'will have been living',
          explanation: 'Use "will have been + verb-ing" for future perfect continuous.'
        },
        {
          question: 'I _____ (finish) the project by Friday.',
          answer: 'will have finished',
          explanation: 'Use "will have + past participle" for future perfect.'
        },
        {
          question: 'She _____ (cook) dinner for four hours by 8 PM.',
          answer: 'will have been cooking',
          explanation: 'Use "will have been + verb-ing" for future perfect continuous.'
        },
        {
          question: 'They _____ (build) the house for eight months by December.',
          answer: 'will have been building',
          explanation: 'Use "will have been + verb-ing" for future perfect continuous.'
        },
        {
          question: 'He _____ (practice) piano for six years by next year.',
          answer: 'will have been practicing',
          explanation: 'Use "will have been + verb-ing" for future perfect continuous.'
        },
        {
          question: 'We _____ (travel) for three weeks by the time we return.',
          answer: 'will have been traveling',
          explanation: 'Use "will have been + verb-ing" for future perfect continuous.'
        },
        {
          question: 'I _____ (read) this book for two months by next week.',
          answer: 'will have been reading',
          explanation: 'Use "will have been + verb-ing" for future perfect continuous.'
        }
      ]
    },
    {
      id: 'p6-topic-9',
      title: 'Discourse Markers and Connectors',
      description: 'Learning to use advanced discourse markers and connectors',
      purpose: 'To teach students how to use advanced discourse markers and connectors to create coherent and well-structured writing.',
      questions: [
        {
          question: 'Complete: "I like swimming. _____, I also enjoy running."',
          answer: 'Furthermore',
          explanation: 'Use "Furthermore" to add more information to what was said before.'
        },
        {
          question: 'Fill in the blank: "The weather was bad. _____, we decided to stay home."',
          answer: 'Therefore',
          explanation: 'Use "Therefore" to show the result or consequence of what was said before.'
        },
        {
          question: 'Complete: "I studied hard. _____, I passed the exam."',
          answer: 'Consequently',
          explanation: 'Use "Consequently" to show the result of what was said before.'
        },
        {
          question: 'Fill in the blank: "He was tired. _____, he continued working."',
          answer: 'However',
          explanation: 'Use "However" to show contrast with what was said before.'
        },
        {
          question: 'Complete: "The food was delicious. _____, the service was excellent."',
          answer: 'Moreover',
          explanation: 'Use "Moreover" to add more information to what was said before.'
        },
        {
          question: 'Fill in the blank: "It was raining. _____, we went for a walk."',
          answer: 'Nevertheless',
          explanation: 'Use "Nevertheless" to show contrast despite what was said before.'
        },
        {
          question: 'Complete: "She was sick. _____, she went to school."',
          answer: 'However',
          explanation: 'Use "However" to show contrast with what was said before.'
        },
        {
          question: 'Fill in the blank: "He failed the test. _____, he didn\'t study."',
          answer: 'This is because',
          explanation: 'Use "This is because" to explain the reason for what was said before.'
        },
        {
          question: 'Complete: "The movie was long. _____, it was very interesting."',
          answer: 'However',
          explanation: 'Use "However" to show contrast with what was said before.'
        },
        {
          question: 'Fill in the blank: "She worked hard. _____, she got promoted."',
          answer: 'As a result',
          explanation: 'Use "As a result" to show the consequence of what was said before.'
        }
      ]
    },
    {
      id: 'p6-topic-10',
      title: 'Presentation Skills',
      description: 'Learning to present information effectively and confidently',
      purpose: 'To teach students how to present information effectively using clear structure, appropriate language, and confident delivery.',
      questions: [
        {
          question: 'What should you do at the beginning of a presentation?',
          answer: 'introduce yourself and topic',
          explanation: 'Begin a presentation by introducing yourself and the topic you will be discussing.'
        },
        {
          question: 'What should you include in a presentation outline?',
          answer: 'main points',
          explanation: 'Include main points in a presentation outline to guide your audience through the content.'
        },
        {
          question: 'What should you use to support your main points?',
          answer: 'examples and evidence',
          explanation: 'Use examples and evidence to support your main points and make them more convincing.'
        },
        {
          question: 'What should you do to maintain audience attention?',
          answer: 'use clear language',
          explanation: 'Use clear language to maintain audience attention and ensure understanding.'
        },
        {
          question: 'What should you include in your conclusion?',
          answer: 'summary of main points',
          explanation: 'Include a summary of main points in your conclusion to reinforce key messages.'
        },
        {
          question: 'What should you do to engage your audience?',
          answer: 'ask questions',
          explanation: 'Ask questions to engage your audience and encourage participation.'
        },
        {
          question: 'What should you use to organize your presentation?',
          answer: 'transitions',
          explanation: 'Use transitions to organize your presentation and help the audience follow your structure.'
        },
        {
          question: 'What should you do to show confidence?',
          answer: 'maintain eye contact',
          explanation: 'Maintain eye contact to show confidence and connect with your audience.'
        },
        {
          question: 'What should you avoid in a presentation?',
          answer: 'reading from notes',
          explanation: 'Avoid reading from notes in a presentation - speak naturally and engage with your audience.'
        },
        {
          question: 'What should you do to end a presentation effectively?',
          answer: 'thank the audience',
          explanation: 'Thank the audience at the end of a presentation to show appreciation and conclude politely.'
        }
      ]
    }
  ]
}; 